import { computed, Injectable, signal, Signal } from '@angular/core';
import {
  MediaFile,
  MediaFitMode,
  MediaRotation,
  MediaSort,
  MediaSortField,
  MediaState,
  MediaStatus,
  MediaTheme,
  MediaTypeFilter,
  MediaViewerError,
  MediaViewStyle,
} from '../models/media.model';

/**
 * Per-instance reactive store for one gallery + lightbox. Provided in the component's `providers`
 * array so every `<app-media-viewer>` gets an isolated state object; the component template and the
 * feature services read/write through this single source of truth, which keeps the component thin
 * and the services free of DOM concerns. Mirrors `PdfStateService`.
 */
@Injectable()
export class MediaStateService {
  private readonly _items = signal<readonly MediaFile[]>([]);
  private readonly _search = signal('');
  private readonly _typeFilter = signal<MediaTypeFilter>('all');
  private readonly _extensions = signal<readonly string[]>([]);
  private readonly _sort = signal<MediaSort>({ field: 'date', dir: 'desc' });
  private readonly _view = signal<MediaViewStyle>('grid');
  private readonly _theme = signal<MediaTheme>('light');
  private readonly _page = signal(1);
  private readonly _pageSize = signal(24);

  private readonly _lightboxOpen = signal(false);
  private readonly _activeIndex = signal(0);
  private readonly _zoom = signal(100);
  private readonly _rotation = signal<MediaRotation>(0);
  private readonly _fitMode = signal<MediaFitMode>('contain');
  private readonly _fullscreen = signal(false);
  private readonly _mediaStatus = signal<MediaStatus>('idle');
  private readonly _mediaError = signal<MediaViewerError | null>(null);

  readonly items = this._items.asReadonly();
  readonly search = this._search.asReadonly();
  readonly typeFilter = this._typeFilter.asReadonly();
  readonly extensions = this._extensions.asReadonly();
  readonly sort = this._sort.asReadonly();
  readonly view = this._view.asReadonly();
  readonly theme = this._theme.asReadonly();
  readonly page = this._page.asReadonly();
  readonly pageSize = this._pageSize.asReadonly();

  readonly lightboxOpen = this._lightboxOpen.asReadonly();
  readonly activeIndex = this._activeIndex.asReadonly();
  readonly zoom = this._zoom.asReadonly();
  readonly rotation = this._rotation.asReadonly();
  readonly fitMode = this._fitMode.asReadonly();
  readonly fullscreen = this._fullscreen.asReadonly();
  readonly mediaStatus = this._mediaStatus.asReadonly();
  readonly mediaError = this._mediaError.asReadonly();

  /** Every distinct extension present in the source set, for the filter dropdown. */
  readonly availableExtensions = computed(() =>
    Array.from(new Set(this._items().map((item) => item.extension.toLowerCase()))).sort((a, b) =>
      a.localeCompare(b)
    )
  );

  /** Search + type + extension facets applied, then sorted. Drives the gallery. */
  readonly filtered = computed(() => {
    const term = this._search().trim().toLowerCase();
    const type = this._typeFilter();
    const exts = this._extensions();
    const matches = this._items().filter((item) => {
      if (type !== 'all' && item.type !== type) {
        return false;
      }
      if (exts.length > 0 && !exts.includes(item.extension.toLowerCase())) {
        return false;
      }
      if (term) {
        const haystack = `${item.name} ${item.extension}`.toLowerCase();
        return haystack.includes(term);
      }
      return true;
    });
    return this.applySort(matches, this._sort());
  });

  /** The slice of {@link filtered} for the current page (all of it when paging is disabled). */
  readonly paged = computed(() => {
    const size = this._pageSize();
    const all = this.filtered();
    if (size <= 0) {
      return all;
    }
    const start = (this._page() - 1) * size;
    return all.slice(start, start + size);
  });

  readonly totalCount = computed(() => this._items().length);
  readonly filteredCount = computed(() => this.filtered().length);
  readonly pageCount = computed(() => {
    const size = this._pageSize();
    return size <= 0 ? 1 : Math.max(1, Math.ceil(this.filteredCount() / size));
  });
  readonly imageCount = computed(() => this._items().filter((i) => i.type === 'image').length);
  readonly videoCount = computed(() => this._items().filter((i) => i.type === 'video').length);

  /** The item currently open in the lightbox, or `null` when closed / out of range. */
  readonly activeItem = computed<MediaFile | null>(() => {
    const list = this.filtered();
    const index = this._activeIndex();
    return index >= 0 && index < list.length ? list[index] : null;
  });

  readonly canGoPrevious = computed(() => this._activeIndex() > 0);
  readonly canGoNext = computed(() => this._activeIndex() < this.filtered().length - 1);
  readonly isImageActive = computed(() => this.activeItem()?.type === 'image');
  readonly isMediaLoading = computed(() => this._mediaStatus() === 'loading');
  readonly hasMediaError = computed(() => this._mediaStatus() === 'error');

  /** Immutable snapshot used by toolbar predicates and tests. */
  readonly snapshot: Signal<MediaState> = computed(() => ({
    items: this._items(),
    search: this._search(),
    typeFilter: this._typeFilter(),
    extensions: this._extensions(),
    sort: this._sort(),
    view: this._view(),
    theme: this._theme(),
    lightboxOpen: this._lightboxOpen(),
    activeIndex: this._activeIndex(),
    zoom: this._zoom(),
    rotation: this._rotation(),
    fitMode: this._fitMode(),
    fullscreen: this._fullscreen(),
    mediaStatus: this._mediaStatus(),
    mediaError: this._mediaError(),
  }));

  // ---- Gallery mutations ---------------------------------------------------------------------
  setItems(items: readonly MediaFile[]): void {
    this._items.set(items ?? []);
    this.clampPage();
    this.clampActiveIndex();
  }

  setSearch(term: string): void {
    this._search.set(term ?? '');
    this._page.set(1);
  }

  setTypeFilter(filter: MediaTypeFilter): void {
    this._typeFilter.set(filter);
    this._page.set(1);
  }

  setExtensions(extensions: readonly string[]): void {
    this._extensions.set((extensions ?? []).map((e) => e.toLowerCase()));
    this._page.set(1);
  }

  setSort(sort: MediaSort): void {
    this._sort.set(sort);
  }

  setSortField(field: MediaSortField): void {
    const current = this._sort();
    // Toggle direction when re-selecting the same field; otherwise start ascending.
    let dir: 'asc' | 'desc' = 'asc';
    if (current.field === field) {
      dir = current.dir === 'asc' ? 'desc' : 'asc';
    }
    this._sort.set({ field, dir });
  }

  setView(view: MediaViewStyle): void {
    this._view.set(view);
  }

  toggleView(): MediaViewStyle {
    const next: MediaViewStyle = this._view() === 'grid' ? 'list' : 'grid';
    this._view.set(next);
    return next;
  }

  setTheme(theme: MediaTheme): void {
    this._theme.set(theme);
  }

  toggleTheme(): MediaTheme {
    const next: MediaTheme = this._theme() === 'dark' ? 'light' : 'dark';
    this._theme.set(next);
    return next;
  }

  setPage(page: number): void {
    this._page.set(Math.min(this.pageCount(), Math.max(1, Math.round(page))));
  }

  setPageSize(size: number): void {
    this._pageSize.set(Math.max(0, Math.round(size)));
    this.clampPage();
  }

  // ---- Lightbox mutations --------------------------------------------------------------------
  openLightbox(index: number): void {
    const list = this.filtered();
    if (list.length === 0) {
      return;
    }
    this._activeIndex.set(Math.min(list.length - 1, Math.max(0, index)));
    this._lightboxOpen.set(true);
    this.resetViewport();
    this.beginMediaLoad();
  }

  closeLightbox(): void {
    this._lightboxOpen.set(false);
    this._fullscreen.set(false);
    this._mediaStatus.set('idle');
    this._mediaError.set(null);
  }

  goToIndex(index: number): void {
    const list = this.filtered();
    if (list.length === 0) {
      return;
    }
    const wrapped = Math.min(list.length - 1, Math.max(0, index));
    if (wrapped !== this._activeIndex()) {
      this._activeIndex.set(wrapped);
      this.resetViewport();
      this.beginMediaLoad();
    }
  }

  next(): void {
    if (this.canGoNext()) {
      this.goToIndex(this._activeIndex() + 1);
    }
  }

  previous(): void {
    if (this.canGoPrevious()) {
      this.goToIndex(this._activeIndex() - 1);
    }
  }

  setZoom(zoom: number): void {
    this._zoom.set(Math.round(zoom));
  }

  setFitMode(mode: MediaFitMode): void {
    this._fitMode.set(mode);
  }

  setRotation(rotation: MediaRotation): void {
    this._rotation.set(rotation);
  }

  rotate(direction: 'left' | 'right'): MediaRotation {
    const delta = direction === 'right' ? 90 : -90;
    const next = ((((this._rotation() + delta) % 360) + 360) % 360) as MediaRotation;
    this._rotation.set(next);
    return next;
  }

  setFullscreen(active: boolean): void {
    this._fullscreen.set(active);
  }

  beginMediaLoad(): void {
    this._mediaStatus.set('loading');
    this._mediaError.set(null);
  }

  setMediaLoaded(): void {
    this._mediaStatus.set('loaded');
  }

  setMediaError(error: MediaViewerError): void {
    this._mediaError.set(error);
    this._mediaStatus.set('error');
  }

  /** Reset zoom/rotation/fit to the neutral viewport (called on open and on navigation). */
  resetViewport(): void {
    this._zoom.set(100);
    this._rotation.set(0);
  }

  private applySort(list: readonly MediaFile[], sort: MediaSort): MediaFile[] {
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => factor * this.compare(a, b, sort.field));
  }

  private compare(a: MediaFile, b: MediaFile, field: MediaSortField): number {
    switch (field) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'size':
        return a.size - b.size;
      case 'type':
        return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      case 'date':
      default:
        return this.toTime(a.createdDate) - this.toTime(b.createdDate);
    }
  }

  private toTime(value: string | Date): number {
    return value instanceof Date ? value.getTime() : new Date(value).getTime();
  }

  private clampPage(): void {
    if (this._page() > this.pageCount()) {
      this._page.set(this.pageCount());
    }
  }

  /** Keep the open lightbox in sync when the source set shrinks (e.g. the host deletes the active
   * file): snap to the new last item, or close if nothing remains — never leave activeItem() null. */
  private clampActiveIndex(): void {
    if (!this._lightboxOpen()) {
      return;
    }
    const count = this.filtered().length;
    if (count === 0) {
      this.closeLightbox();
      return;
    }
    if (this._activeIndex() > count - 1) {
      this._activeIndex.set(count - 1);
      this.resetViewport();
      this.beginMediaLoad();
    }
  }
}
